import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useT } from '../i18n/I18nProvider';
import { Banner } from './states';

interface Verification {
  ok: boolean;
  count: number;
  brokenAtSeq?: number;
  reason?: string;
}

/**
 * Verifies the tenant's tamper-evident audit chain on demand. A pass is evidence the audit
 * log has not been altered; a failure names the first broken event. Visible to anyone who
 * can read the audit log.
 */
export function AuditIntegrityPanel(): JSX.Element {
  const t = useT();
  const { can } = useAuth();
  const [result, setResult] = useState<Verification | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!can('audit:read')) return <></>;

  const verify = (): void => {
    setError(null);
    setResult(null);
    setBusy(true);
    void (async () => {
      try {
        setResult(await api.get<Verification>('/operations/audit-integrity'));
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className="card">
      <h2>{t('auditIntegrity.title')}</h2>
      <p>{t('auditIntegrity.intro')}</p>
      {error ? <Banner variant="error">{error}</Banner> : null}
      {result ? (
        result.ok ? (
          <Banner variant="success">
            {t('auditIntegrity.ok').replace('{count}', String(result.count))}
          </Banner>
        ) : (
          <Banner variant="error">
            {t('auditIntegrity.broken')
              .replace('{seq}', String(result.brokenAtSeq ?? '?'))
              .replace('{reason}', result.reason ?? '')}
          </Banner>
        )
      ) : null}
      <button type="button" className="btn" disabled={busy} onClick={verify}>
        {busy ? t('auditIntegrity.verifying') : t('auditIntegrity.verify')}
      </button>
    </div>
  );
}
